export default class BaseCarousel {
	/**
	 * @var {HTMLElement}
	 */
	carousel = null;

	/**
	 * @var {HTMLElement}
	 */
	carouselWrapper = null;

	/**
	 * @var {HTMLElement[]}
	 */
	slides = [];

	/**
	 * @var {Number}
	 */
	originalSlides = 0;

	/**
	 * @var {Number}
	 */
	currentDotIndex = 0;

	/**
	 * @var {Number}
	 */
	currentSlide = 0;

	/**
	 * @var {bool}
	 */
	lastSlide = false;

	/**
	 * @var {Boolean}
	 */
	fade = false;

	/**
	 * @var {Boolean}
	 */
	overflow = false;

	/**
	 * @var {Number}
	 */
	autoplay = 0;

	/**
	 * @var {Boolean}
	 */
	infinite = false;

	/**
	 * @var {String}
	 */
	align = 'left';

	/**
	 * @var {Number}
	 */
	slidesInView = 1;

	/**
	 * @var {ResizeObserver}
	 */
	resizeObserver = null;

	/**
	 * @var {Boolean}
	 */
	preview = false;

	/**
	 * @var {Boolean}
	 */
	isDragging = false;

	/**
	 * @var {Number}
	 */
	startX = 0;

	/**
	 * @var {Number}
	 */
	startY = 0;

	/**
	 * @var {Number}
	 */
	initialScroll = 0;

	/**
	 * @var {Number}
	 */
	currentTranslate = 0;

	/**
	 * @var {Number}
	 */
	slideXTotal = 0;

	/**
	 * @var {Number}
	 */
	prevTranslate = 0;

	/**
	 * @var {Boolean}
	 */
	isChanging = false;

	dragThresholdExceeded = false;

	/**
	 * @var {Boolean}
	 */
	dragged = false;

	/**
	 * constructor
	 * @var {Boolean}
	 * @var {Number}
	 * @var {Boolean}
	 * @var {String}
	 */
	constructor(fade, overflow, autoplay, infinite, align = 'left', slidesInView = 1, preview = false) {
		this.fade = fade;
		this.overflow = overflow;
		this.autoplay = parseInt(autoplay * 1000);
		this.infinite = infinite;
		this.align = align;
		this.slidesInView = Math.ceil(slidesInView);
		this.preview = preview;
	}

	/**
	 * init
	 * @return void
	 */
	init() {
		// Bind event handlers here (not at class property level) so they use the Alpine-proxied 'this'
		this.onDragStart = this.onDragStart.bind(this);
		this.onDragMove = this.onDragMove.bind(this);
		this.onDragEnd = this.onDragEnd.bind(this);

		this.carousel = this.$el.querySelector('.Carousel');
		this.carouselWrapper = this.$el.querySelector('.Carousel__Wrapper');

		if (!this.carousel) return;

		this.slides = [...this.carousel.children];

		this.originalSlides = this.slides.length;

		// Calculate initial slidesInView based on actual visible slides
		this.updateSlidesInView();

		// Set up ResizeObserver to recalculate slidesInView when container size changes
		this.resizeObserver = new ResizeObserver(() => {
			this.updateSlidesInView();
		});
		this.resizeObserver.observe(this.carouselWrapper);

		if (this.infinite && !this.preview) {
			this.cloneSlides();
		}

		// Disable dragging on all <a> tags within the slides
		this.slides.forEach((slide) => {
			if (slide.tagName === 'A') {
				slide.setAttribute('draggable', 'false');
				slide.addEventListener('click', (event) => {
					if (this.dragged) {
						event.preventDefault();
						this.dragged = false;
					}
				});
				return;
			}
			const links = slide.querySelectorAll('a');
			links.forEach((link) => {
				link.setAttribute('draggable', 'false');
				link.addEventListener('click', (event) => {
					if (this.dragged) {
						event.preventDefault();
						this.dragged = false;
					}
				});
			});
		});

		if (this.fade && !this.preview) {
			this.slides.forEach((slide, index) => {
				slide.style.position = 'absolute';
				slide.style.transition = 'opacity 0.5s';
				slide.style.left = 0;
				slide.style.top = 0;

				if (index > 0) {
					slide.style.opacity = 0;
				}
			});

			this.slides.forEach((slide, index) => {
				if (index !== 0) {
					slide.style.pointerEvents = 'none';
				}
			});
		}

		if (
			!this.infinite &&
			this.overflow &&
			this.align !== 'full' &&
			this.align !== 'center' &&
			this.align !== 'wide' &&
			this.slides.length > this.slidesInView
		) {
			this.containerResize();
		}

		pegasus.onResizeFinished(() => {
			this.goToSlide(this.currentSlide);
			this.getSlideXTotal();
			this.isChanging = false;
		}, true);

		// Add event listeners for dragging
		if (!this.preview && !this.fade) {
			this.carousel.addEventListener('mousedown', this.onDragStart);
			this.carousel.addEventListener('touchstart', this.onDragStart, {
				passive: true,
			});
		}

		this.carousel.addEventListener('transitionend', () => {
			this.handleInfiniteScroll();
			setTimeout(() => {
				this.isChanging = false;
			}, 300);
		});

		this.isChanging = false;
	}

	initAutoplay() {
		if (this.autoplay && !this.preview) {
			setInterval(() => {
				if (this.isDragging) return;
				this.nextSlide();
			}, this.autoplay);
		} else {
			return;
		}
	}

	getSlideXTotal() {
		// Loop through this.slides and get the total width of all the slides added together
		this.slideXTotal = 0;
		this.slides.forEach((slide) => {
			this.slideXTotal += slide.offsetWidth;
		});
	}

	/**
	 * Clone sets of slides to create an infinite looping effect with multiple visible slides
	 */
	cloneSlides() {
		const firstClones = this.slides.slice(0, this.slidesInView).map((slide) => slide.cloneNode(true));
		const lastClones = this.slides.slice(-this.slidesInView).map((slide) => slide.cloneNode(true));

		firstClones.forEach((clone) => this.carousel.appendChild(clone));
		lastClones.forEach((clone) => this.carousel.insertBefore(clone, this.slides[0]));

		this.slides = [...this.carousel.children]; // Update slides array
		this.currentSlide = this.slidesInView; // Start at the first real slide set
		this.goToSlide(this.currentSlide, false); // Position slider to start
	}

	handleInfiniteScroll() {
		if (!this.infinite || this.preview) return;

		let slideOffset = 0;

		if (this.align === 'center') {
			slideOffset = Math.floor(this.slidesInView / 2);
		}

		let firstSlide = 0 + slideOffset;
		let lastSlide = this.slides.length - this.slidesInView;

		if (this.currentSlide === firstSlide) {
			const goTo = this.slides.length - this.slidesInView * 2 + slideOffset;
			this.goToSlide(goTo, false, true);
			this.isChanging = false;
		}

		if (this.currentSlide === lastSlide) {
			this.goToSlide(this.slidesInView, false, true);
			this.isChanging = false;
		}
	}

	/**
	 * nextSlide
	 * @param {Number}
	 * @return void
	 */
	nextSlide(slidesToScroll = 1) {
		// Ensure we have the total width of the slides, can be 0 when slider is hidden e.g in a tab
		if (this.slideXTotal == 0) this.getSlideXTotal();
		// Check if the last slide is fully visible
		const maxTranslate = this.slideXTotal - this.carouselWrapper.offsetWidth;

		if (Math.abs(this.getSliderCurrentTranslate()) >= maxTranslate && !this.infinite) {
			this.goToSlide(0);
			return;
		}

		this.goToSlide(this.currentSlide + slidesToScroll);
	}

	/**
	 * previousSlide
	 * @param {Number}
	 * @return void
	 */
	previousSlide(slidesToScroll = 1) {
		if (this.currentSlide - slidesToScroll < 0) {
			return;
		}
		this.goToSlide(this.currentSlide - slidesToScroll);
	}

	/**
	 * goToSlide
	 * @param {Number} slide
	 * @return void
	 */
	goToSlide(slide, animate = true, infiniteJump = false) {
		if (this.isChanging) {
			return;
		}

		this.currentSlide = slide;

		const realSlideIndex = (this.currentSlide - this.slidesInView) % this.originalSlides;
		this.currentDotIndex = realSlideIndex < 0 ? this.originalSlides + realSlideIndex : realSlideIndex;

		if (this.fade && !this.preview) {
			this.fadeSlide(slide, animate);
			this.slides.forEach((slide, index) => {
				if (index === this.currentSlide) {
					slide.style.pointerEvents = 'auto';
				} else {
					slide.style.pointerEvents = 'none';
				}
			});
		} else {
			this.scrollSlide(slide, animate, infiniteJump);
		}

		this.setCurrentSlideClass();

		window.dispatchEvent(
			new CustomEvent('updateslideindex', {
				detail: { slideIndex: this.getCurrentSlide() },
			}),
		);

		this.isChanging = false;
	}

	scrollSlide(slide, animate, infiniteJump) {
		if (slide + 1 === this.slides.length) {
			this.lastSlide = true;
		} else {
			this.lastSlide = false;
		}

		const selectedSlide = this.slides[slide];

		if (!selectedSlide) return;

		// get the x position of the selected slide
		let slideX = selectedSlide.getBoundingClientRect().left;
		const currentTranslate = this.getSliderCurrentTranslate();
		// get the x position of the this.carouselWrapper
		let wrapperX = this.carouselWrapper.getBoundingClientRect().left;

		if (this.align === 'center') {
			slideX = selectedSlide.getBoundingClientRect().left + selectedSlide.offsetWidth / 2;
			wrapperX = this.carouselWrapper.getBoundingClientRect().left + this.carouselWrapper.offsetWidth / 2;
		}

		if (this.align === 'right') {
			slideX = selectedSlide.getBoundingClientRect().right;
			wrapperX = this.carouselWrapper.getBoundingClientRect().left + this.carouselWrapper.offsetWidth;
		}

		// calculate the distance to translate
		let distance = wrapperX - slideX + currentTranslate;

		if (slide === 0) {
			distance = 0;
		}

		if (slide === this.slides.length - 1) {
			slideX = selectedSlide.getBoundingClientRect().right;
			wrapperX = this.carouselWrapper.getBoundingClientRect().left + this.carouselWrapper.offsetWidth;
			distance = wrapperX - slideX + currentTranslate;
		}

		const maxTranslate = this.slideXTotal - this.carouselWrapper.offsetWidth;

		if (maxTranslate > 0 && Math.abs(distance) >= maxTranslate && !this.infinite) {
			distance = -Math.abs(maxTranslate);
			this.lastSlide = true;
		}

		this.isChanging = true;

		if (!animate || infiniteJump) {
			this.carousel.classList.add('no-transition');
		}

		this.carousel.style.transform = `translateX(${distance}px)`;

		this.currentTranslate = distance;

		if (!animate || infiniteJump) {
			setTimeout(() => {
				this.carousel.offsetWidth; // Trigger reflow
				this.carousel.classList.remove('no-transition');
			}, 50);
		}
	}

	fadeSlide(slide, animate) {
		if (slide + 1 === this.slides.length) {
			this.lastSlide = true;
		} else {
			this.lastSlide = false;
		}

		this.carousel.style.height = `${this.slides[slide].offsetHeight}px`;

		this.slides.forEach((slide, index) => {
			if (index === this.currentSlide) {
				slide.style.opacity = 1;
			} else {
				slide.style.opacity = 0;
			}
		});
	}

	/**
	 * Handle drag start
	 */
	onDragStart(event) {
		if (event.target.tagName === 'A' || event.target.tagName === 'VIDEO') {
			event.preventDefault();
		}

		this.isDragging = true;
		this.carousel.classList.add('dragging');
		this.startX = event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
		this.startY = event.type.includes('mouse') ? event.pageY : event.touches[0].clientY;
		this.prevTranslate = this.getSliderCurrentTranslate();
		this.dragThresholdExceeded = false;
		this.dragged = false;

		// Store the initial scroll position
		this.initialScroll = window.scrollY;

		// Attach move and end listeners
		this.carouselWrapper.addEventListener('mousemove', this.onDragMove);
		this.carouselWrapper.addEventListener('touchmove', this.onDragMove, {
			passive: true,
		});
		this.carouselWrapper.addEventListener('mouseup', this.onDragEnd);
		this.carouselWrapper.addEventListener('touchend', this.onDragEnd);
	}

	/**
	 * Handle drag move - continuously update translation to follow cursor
	 */
	onDragMove(event) {
		if (!this.isDragging) return;

		const currentX = event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
		const currentY = event.type.includes('mouse') ? event.pageY : event.touches[0].clientY;

		// Calculate X and Y movement
		const deltaX = currentX - this.startX;
		const deltaY = currentY - this.startY;

		const distanceMoved = currentX - this.startX;

		// Check if the threshold has been exceeded
		if (!this.dragThresholdExceeded && Math.abs(distanceMoved) < 10) {
			return;
		}

		this.dragThresholdExceeded = true;

		// If vertical scrolling is detected, stop the drag
		if (Math.abs(deltaY) > Math.abs(deltaX) * 1.5) {
			this.onDragEnd();
			return;
		}

		// Prevent page scroll while dragging horizontally
		if (Math.abs(deltaX) > 10) {
			window.scrollTo(0, this.initialScroll);
		}

		// Update the current translate to follow the mouse movement exactly
		this.currentTranslate = this.prevTranslate + distanceMoved;

		if (window.outerWidth >= 768) {
			this.carousel.style.transform = `translateX(${this.currentTranslate}px)`;
		}

		// If the mouse leaves this.carouselWrapper, end the drag
		this.carouselWrapper.addEventListener('mouseleave', this.onDragEnd);
		this.carouselWrapper.addEventListener('touchleave', this.onDragEnd);
	}

	/**
	 * Handle drag end and snap to nearest slide
	 */
	onDragEnd() {
		if (!this.dragThresholdExceeded) {
			// If the threshold was not exceeded, treat it as a click and do nothing
			this.isDragging = false;
			this.carousel.classList.remove('dragging');
			this.carouselWrapper.removeEventListener('mousemove', this.onDragMove);
			this.carouselWrapper.removeEventListener('touchmove', this.onDragMove, { passive: true });
			this.carouselWrapper.removeEventListener('mouseleave', this.onDragEnd);
			this.carouselWrapper.removeEventListener('touchleave', this.onDragEnd, { passive: true });
			return;
		}

		this.isDragging = false;
		this.dragged = true;

		// Determine the closest slide to snap to based on the current translation
		const closestSlideIndex = this.getClosestSlideIndex(this.currentTranslate);

		// Snap to the closest slide
		this.goToSlide(closestSlideIndex);

		this.carousel.classList.remove('dragging');

		window.dispatchEvent(
			new CustomEvent('updateslideindex', {
				detail: { slideIndex: this.getCurrentSlide() },
			}),
		);

		this.carouselWrapper.removeEventListener('mousemove', this.onDragMove);
		this.carouselWrapper.removeEventListener('touchmove', this.onDragMove, {
			passive: true,
		});
		this.carouselWrapper.removeEventListener('mouseleave', this.onDragEnd);
		this.carouselWrapper.removeEventListener('touchleave', this.onDragEnd, {
			passive: true,
		});

		this.isChanging = false;
	}

	/**
	 * Determine the index of the slide closest to the current position
	 * @param {Number} currentTranslate - The current translation position of the slider
	 * @return {Number} The index of the closest slide
	 */
	getClosestSlideIndex(currentTranslate) {
		let closestIndex = this.currentSlide;
		let minDistance = Infinity;

		this.slides.forEach((slide, index) => {
			// Calculate the slide’s position relative to the carousel wrapper
			let slidePosition = slide.offsetLeft - this.carouselWrapper.offsetLeft;

			if (this.align === 'center') {
				slidePosition = slide.offsetLeft + slide.offsetWidth / 2 - (this.carouselWrapper.offsetLeft + this.carouselWrapper.offsetWidth / 2);
			}

			if (this.align === 'right') {
				slidePosition = slide.offsetLeft + slide.offsetWidth - (this.carouselWrapper.offsetLeft + this.carouselWrapper.offsetWidth);
			}

			// Calculate the distance from current position to slide position
			const distance = Math.abs(slidePosition + currentTranslate);

			// Update the closest slide if this one is closer
			if (distance < minDistance) {
				minDistance = distance;
				closestIndex = index;
			}
		});

		return closestIndex;
	}

	/**
	 * getSliderCurrentTranslate
	 * @return {Number}
	 */
	getSliderCurrentTranslate() {
		const style = window.getComputedStyle(this.carousel);
		const matrix = new WebKitCSSMatrix(style.transform);

		return matrix.m41;
	}

	/**
	 * containerResize
	 *
	 * Description: In order to get the slider to break out of its parent container
	 * we need to set the width of the slider to be larger than the container.
	 *
	 * @return void
	 */
	containerResize() {
		const container = this.$root.closest('.container');

		if (!container || window.outerWidth < 768) return;

		if (!this.carousel) return;

		// We need to get the width of the container excluding padding
		let widthWithPaddings = container.clientWidth;
		const elementComputedStyle = window.getComputedStyle(container, null);

		const containerWidth = widthWithPaddings - parseFloat(elementComputedStyle.paddingLeft) - parseFloat(elementComputedStyle.paddingRight);

		const containerVw = (containerWidth / window.innerWidth) * 100;

		// If the align is full, center or wide we need to set the slider to be the full width of the container
		let divider = this.align === 'full' || this.align === 'center' || this.align === 'wide' ? 1 : 2;

		const carouselWidth = 100 - (100 - containerVw) / divider;

		this.carousel.style.width = `${carouselWidth}vw`;
	}

	setCurrentSlideClass() {
		this.slides.forEach((slide, index) => {
			if (index === this.currentSlide) {
				slide.classList.add('is-selected');
			} else {
				slide.classList.remove('is-selected');
			}
		});
	}

	getSlideCount() {
		return this.originalSlides;
	}

	getCurrentSlide() {
		return this.infinite ? this.currentDotIndex : this.currentSlide;
	}

	/**
	 * Calculate how many slides are fully visible within the container
	 * @return {Number} The number of fully visible slides
	 */
	calculateVisibleSlides() {
		if (!this.carouselWrapper || !this.slides.length) return 1;

		const containerWidth = this.carouselWrapper.offsetWidth;
		const firstSlide = this.slides[0];

		if (!firstSlide) return 1;

		// Get the actual rendered width of a slide including any gaps/margins
		const slideStyle = getComputedStyle(firstSlide);
		const slideWidth = firstSlide.offsetWidth + parseFloat(slideStyle.marginLeft || 0) + parseFloat(slideStyle.marginRight || 0);

		if (slideWidth <= 0) return 1;

		// Calculate how many full slides fit in the container
		const visibleSlides = Math.floor(containerWidth / slideWidth);

		return Math.max(1, visibleSlides);
	}

	/**
	 * Update slidesInView based on actual visible slides in the container.
	 * Called on resize to keep pagination accurate.
	 * @return {void}
	 */
	updateSlidesInView() {
		const newSlidesInView = this.calculateVisibleSlides();

		if (newSlidesInView !== this.slidesInView) {
			this.slidesInView = newSlidesInView;

			// Ensure current slide is still valid after the change
			const maxSlide = Math.max(0, this.originalSlides - this.slidesInView);
			if (this.currentSlide > maxSlide && !this.infinite) {
				this.goToSlide(maxSlide, false);
			}

			// Dispatch event to notify of the change
			window.dispatchEvent(
				new CustomEvent('updateslideindex', {
					detail: {
						slideIndex: this.getCurrentSlide(),
						slidesInView: this.slidesInView,
					},
				}),
			);
		}
	}

	/**
	 * Get the total number of pages (slide positions) based on slides in view
	 * @return {Number} The total number of pages
	 */
	getPageCount() {
		return Math.max(1, this.originalSlides - this.slidesInView + 1);
	}

	/**
	 * Get the current page number (1-indexed) for display purposes
	 * @return {Number} The current page number
	 */
	getCurrentPage() {
		const slideIndex = this.infinite ? this.currentDotIndex : this.currentSlide;
		return Math.min(slideIndex + 1, this.getPageCount());
	}
}

document.addEventListener('alpine:init', () => {
	Alpine.data(
		'carousel',
		(fade, overflow, autoplay, infinite, align, slidesInView, preview) => new BaseCarousel(fade, overflow, autoplay, infinite, align, slidesInView, preview),
	);
});

document.addEventListener('alpine:init', () => {
	Alpine.data('carouselDots', () => ({
		currentDot: 0,
	}));
});
